import { InteractionStatus } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useQuery } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import type { ExamsApi } from './api/exams'
import type { LlmApi } from './api/llm'
import type { BrandsApi } from './api/brands'
import { BrandSettings } from './settings/BrandSettings'
import { ExamSettings } from './settings/ExamSettings'
import type { MocksApi } from './api/mocks'
import type { PracticeApi } from './api/practice'
import type { QuestionsApi } from './api/questions'
import type { SupportApi } from './api/support'
import { isAuthResponse } from './auth'
import { AccountMenu } from './AccountMenu'
import { AreaNav } from './AreaNav'
import { ChooseExam } from './ChooseExam'
import { codeOf, forgetExam, rememberExam, rememberedExam, slugOf } from './chosenExam'
import { Sittings } from './exam/Sittings'
import { Topics } from './exam/Topics'
import { ExamSwitcher } from './ExamSwitcher'
import { Coverage } from './questions/Coverage'
import { QuestionPreview } from './questions/QuestionPreview'
import { QuestionSchema } from './questions/QuestionSchema'
import { Questions } from './questions/Questions'
import { Masthead } from './Masthead'
import { Mocks } from './mocks/Mocks'
import { Practice } from './practice/Practice'
import { PagesMenu } from './PagesMenu'
import { SessionDetails, SignInPrompt } from './Session'
import { LlmSettings } from './settings/LlmSettings'
import { Support } from './support/Support'
import type { AppConfig } from './config'

/**
 * What every screen is handed. The configuration arrives at runtime and the API client is
 * built from it and from the signed-in account, so both are made once, in the app, and
 * carried here rather than reached for out of a module-level singleton -- which is what makes
 * a screen testable with a stand-in.
 */
export interface RouterContext {
  config: AppConfig
  llm: LlmApi
  brands: BrandsApi
  questions: QuestionsApi
  exams: ExamsApi
  mocks: MocksApi
  practice: PracticeApi
  support: SupportApi
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Shell })

/**
 * The front door. An Exam chosen before is gone back to without asking (app:R84); the
 * chooser is for the first visit, and for after that Exam has left the catalogue.
 */
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Not while the tenant's answer is still in the fragment. A redirect replaces the URL,
    // MSAL then finds nothing to read, and a granted sign-in lands back on the prompt.
    if (isAuthResponse()) return

    const remembered = rememberedExam()
    if (remembered) {
      throw redirect({ to: '/$exam', params: { exam: slugOf(remembered) } })
    }
  },
  component: HomeScreen,
})

const llmSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/llm',
  component: LlmSettingsScreen,
})

const brandSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/brands',
  component: function BrandSettingsScreen() {
    const { brands } = rootRoute.useRouteContext()
    return <><h2 className="font-heading text-h2 font-bold text-heading">Brands</h2><div className="mt-6"><BrandSettings api={brands} /></div></>
  },
})

const examSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/exams',
  component: function ExamSettingsScreen() {
    const { exams } = rootRoute.useRouteContext()
    return <><h2 className="font-heading text-h2 font-bold text-heading">Exam Settings</h2><div className="mt-6"><ExamSettings exams={exams} /></div></>
  },
})

const sessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/session',
  component: SessionScreen,
})

/**
 * Everything about one Exam lives under its code: `/tmua/sittings`, `/tmua/questions`.
 * The address is what carries the choice, so a bookmark, a shared link and the back button
 * all say which Exam a screen is about, and no screen asks (app:R84).
 */
const examRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$exam',
  component: ExamLayout,
})

// Empty for now. The route stays because plenty lands on it -- the chooser, the remembered
// Exam on the front door, and the switcher off any screen that is not about an Exam -- so an
// Exam's own address has to render something, even while that something is the masthead alone.
const overviewRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/',
  component: () => null,
})

const sittingsRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/sittings',
  component: SittingsScreen,
})

const topicsRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/topics',
  component: TopicsScreen,
})

const questionsRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/questions',
  component: QuestionsScreen,
})

// Under questions, and named for what it does rather than for the file it takes: the whole
// point of it is that the file is NOT uploaded, and a path reading /upload would say the
// opposite of what the screen does. yaml-preview rather than preview, because the Questions
// menu groups it with Schema under Yaml and the address says the same thing the menu does.
const questionPreviewRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/questions/yaml-preview',
  component: QuestionPreviewScreen,
})

const questionSchemaRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/questions/schema',
  component: QuestionSchemaScreen,
})

const mocksRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/mocks',
  component: MocksScreen,
})

const practiceRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/questions/practice',
  component: PracticeScreen,
})

const coverageRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/questions/coverage',
  component: CoverageScreen,
})

const supportRoute = createRoute({
  getParentRoute: () => examRoute,
  path: '/support',
  component: SupportScreen,
})

export const routeTree = rootRoute.addChildren([
  homeRoute,
  llmSettingsRoute,
  brandSettingsRoute,
  examSettingsRoute,
  sessionRoute,
  examRoute.addChildren([
    overviewRoute,
    sittingsRoute,
    topicsRoute,
    questionsRoute,
    questionPreviewRoute,
    questionSchemaRoute,
    coverageRoute,
    mocksRoute,
    practiceRoute,
    supportRoute,
  ]),
])

export function createAppRouter(context: RouterContext) {
  return createRouter({ routeTree, context })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}

/**
 * The shell, and the one place sign-in is decided.
 *
 * A screen behind this can assume there is an account, which is what lets every page call the
 * API without each one carrying its own signed-out branch. It is courtesy, not a gate: the API
 * refuses an unauthenticated call whatever this renders.
 */
function Shell() {
  const { accounts, inProgress } = useMsal()
  const { config, exams, questions } = rootRoute.useRouteContext()
  const params = useParams({ strict: false })

  // Off an Exam's screens -- the cog's pages -- the areas still point at the Exam last
  // worked on, so the nav does not empty out on the way to LLM Settings and back.
  const remembered = rememberedExam()
  const exam = params.exam ?? (remembered ? slugOf(remembered) : null)

  const signedIn = inProgress === InteractionStatus.None && accounts[0] !== undefined
  const unread = useQuery({
    queryKey: ['feedback-unread', exam],
    queryFn: () => questions.unreadFeedback(codeOf(exam!)),
    enabled: signedIn && exam !== null,
  })

  // Startup is MsalProvider working through handleRedirectPromise: on the way back from the
  // tenant the account is not readable yet, and offering a sign-in button here would start a
  // second sign-in over the top of the one that is landing.
  if (inProgress === InteractionStatus.Startup) {
    return (
      <>
        <Masthead />
        <main className="mx-auto max-w-page px-(--container-gutter) py-8">
          <p>Restoring the session...</p>
        </main>
      </>
    )
  }

  if (!accounts[0]) {
    return (
      <>
        <Masthead>
          <AccountMenu config={config} />
        </Masthead>
        <main className="mx-auto max-w-page px-(--container-gutter) py-8">
          <p className="text-body">Sign in to reach the Admin Site.</p>
          <SignedOutPrompt />
        </main>
      </>
    )
  }

  return (
    <>
      {/* Pages under the cog, then the account to its right -- the far corner is where an
          account control is looked for, and the cog should not move when it gains a menu item. */}
      <Masthead beside={<ExamSwitcher exams={exams} exam={exam} />} nav={<AreaNav exam={exam} unread={unread.data ?? 0} />}>
        <PagesMenu />
        <AccountMenu config={config} />
      </Masthead>
      <main className="mx-auto max-w-page px-(--container-gutter) py-8">
        <Outlet />
      </main>
    </>
  )
}

function SignedOutPrompt() {
  const { config } = rootRoute.useRouteContext()

  return <SignInPrompt config={config} />
}

/**
 * The Exam in the address, checked against the catalogue before any screen under it
 * renders, and remembered once it is known to exist -- a mistyped address must not become
 * the Exam the site opens on next time.
 */
function ExamLayout() {
  const { exam } = examRoute.useParams()
  const { exams } = examRoute.useRouteContext()
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  const code = codeOf(exam)
  const known = catalogue.data?.some((entry) => entry.code === code) ?? null

  useEffect(() => {
    if (known === true) rememberExam(code)
    if (known === false) forgetExam()
  }, [known, code])

  if (catalogue.isPending) {
    return <p className="text-muted">Reading the Exams...</p>
  }

  if (catalogue.isError) {
    return (
      <p role="alert" className="border-l-2 border-red-700 pl-3 text-red-700">
        {catalogue.error.message}
      </p>
    )
  }

  if (!known) {
    return (
      <>
        <h2 className="font-heading text-h2 font-bold text-heading">No Exam called {code}</h2>
        <p className="mt-2 text-body">
          The catalogue has no Exam with that code.{' '}
          <Link to="/" className="text-green-700 underline">
            Choose an Exam
          </Link>
        </p>
      </>
    )
  }

  return <Outlet />
}

function HomeScreen() {
  const { exams } = rootRoute.useRouteContext()

  return <ChooseExam exams={exams} />
}

function SupportScreen() {
  const { exam } = examRoute.useParams()
  const { support } = examRoute.useRouteContext()

  return <Support support={support} exam={codeOf(exam)} />
}

function SittingsScreen() {
  const { exam } = examRoute.useParams()
  const { exams } = examRoute.useRouteContext()

  return <Sittings exams={exams} exam={codeOf(exam)} />
}

function TopicsScreen() {
  const { exam } = examRoute.useParams()
  const { exams } = examRoute.useRouteContext()

  return <Topics exams={exams} exam={codeOf(exam)} />
}

function QuestionsScreen() {
  const { exam } = examRoute.useParams()
  const { questions } = examRoute.useRouteContext()

  return <Questions api={questions} exam={codeOf(exam)} />
}

function QuestionPreviewScreen() {
  const { exam } = examRoute.useParams()
  const { questions } = examRoute.useRouteContext()

  return <QuestionPreview api={questions} exam={codeOf(exam)} />
}

function QuestionSchemaScreen() {
  const { questions } = examRoute.useRouteContext()

  return <QuestionSchema api={questions} />
}

function CoverageScreen() {
  const { exam } = examRoute.useParams()
  const { questions, exams } = examRoute.useRouteContext()

  return <Coverage questions={questions} exams={exams} exam={codeOf(exam)} />
}

function MocksScreen() {
  const { exam } = examRoute.useParams()
  const { mocks } = examRoute.useRouteContext()

  return <Mocks mocks={mocks} exam={codeOf(exam)} />
}

function PracticeScreen() {
  const { exam } = examRoute.useParams()
  const { practice, questions, exams } = examRoute.useRouteContext()
  return <Practice key={exam} api={practice} questions={questions} exams={exams} exam={codeOf(exam)} />
}

function LlmSettingsScreen() {
  const { llm } = rootRoute.useRouteContext()

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">LLM Settings</h2>
      <p className="mt-2 text-body">
        The companies the platform calls through Azure AI Foundry, and the models a Checker may
        pick when verifying a Question.
      </p>
      <div className="mt-6">
        <LlmSettings api={llm} />
      </div>
    </>
  )
}

/** Where this sign-in points and whether the API takes it: diagnostics, not an Exam's business. */
function SessionScreen() {
  const { config } = rootRoute.useRouteContext()

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">Session</h2>

      <dl className="mt-6 grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1 rounded-lg border border-ink-100 bg-card p-6">
        <dt className="whitespace-nowrap text-muted">API</dt>
        <dd className="break-words">{config.apiBase}</dd>
        <dt className="whitespace-nowrap text-muted">Directory</dt>
        <dd className="break-words">{config.entraAuthorityHost}</dd>
      </dl>

      <SessionDetails config={config} />
    </>
  )
}
